import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, DollarSign } from 'lucide-react';
import {
  useAllActiveAgreements,
  useAllPendingAgreements,
  useRegisterPayment,
  useCancelAgreement,
  useActivatePendingAgreement,
} from '../hooks/useAgreementsAndResets';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { ConfirmationModal } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from '@/shared/lib/toast';

type PaymentAgreementStatus = 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

const STATUS_LABELS: Record<PaymentAgreementStatus, string> = {
  PENDING: 'Pendente',
  ACTIVE: 'Ativo',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
};

const getStatusVariant = (status: PaymentAgreementStatus): 'success' | 'default' | 'warning' | 'error' => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'ACTIVE':
      return 'default';
    case 'PENDING':
      return 'warning';
    case 'CANCELLED':
      return 'error';
    default:
      return 'default';
  }
};

export function PaymentAgreementsPage() {
  const [activeTab, setActiveTab] = useState<'PENDING' | 'ACTIVE'>('PENDING');
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAgreementId, setSelectedAgreementId] = useState<string | null>(null);
  const [installments, setInstallments] = useState('');
  const [startDate, setStartDate] = useState('');
  const [notes, setNotes] = useState('');

  const { data: pendingAgreements = [], isLoading: loadingPending } = useAllPendingAgreements();
  const { data: activeAgreements = [], isLoading: loadingActive } = useAllActiveAgreements();

  const registerPayment = useRegisterPayment();
  const cancelAgreement = useCancelAgreement();
  const activatePendingAgreement = useActivatePendingAgreement();

  const handleOpenPayModal = (agreementId: string) => {
    setSelectedAgreementId(agreementId);
    setShowPayModal(true);
  };

  const handlePayInstallment = async () => {
    if (!selectedAgreementId) return;

    try {
      await registerPayment.mutateAsync(selectedAgreementId);
      toast.success('Parcela paga com sucesso');
      setShowPayModal(false);
    } catch (error) {
      toast.error('Erro ao registrar pagamento');
    }
  };

  const handleOpenCancelModal = (agreementId: string) => {
    setSelectedAgreementId(agreementId);
    setShowCancelModal(true);
  };

  const handleCancelAgreement = async () => {
    if (!selectedAgreementId) return;

    try {
      await cancelAgreement.mutateAsync({ id: selectedAgreementId, reason: 'Cancelado pelo gestor' });
      toast.success('Acordo cancelado com sucesso');
      setShowCancelModal(false);
    } catch (error) {
      toast.error('Erro ao cancelar acordo');
    }
  };

  const handleOpenActivateModal = (agreementId: string) => {
    setSelectedAgreementId(agreementId);
    setInstallments('');
    setStartDate('');
    setNotes('');
    setShowActivateModal(true);
  };

  const handleActivateAgreement = async () => {
    if (!selectedAgreementId || !installments || !startDate) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      await activatePendingAgreement.mutateAsync({
        id: selectedAgreementId,
        data: {
          installments: parseInt(installments),
          startDate,
          notes,
        },
      });
      toast.success('Acordo ativado com sucesso');
      setShowActivateModal(false);
    } catch (error) {
      toast.error('Erro ao ativar acordo');
    }
  };

  if (loadingPending || loadingActive) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  const tabs = [
    { label: `Pendentes (${pendingAgreements.length})`, value: 'PENDING' as const },
    { label: `Ativos (${activeAgreements.length})`, value: 'ACTIVE' as const },
  ];

  const displayAgreements = activeTab === 'PENDING' ? pendingAgreements : activeAgreements;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Acordos de Pagamento</h1>
          <p className="text-muted-foreground">Gestão de parcelamentos de prejuízos</p>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="pt-6">
          <TabFilter
            tabs={tabs}
            currentTab={activeTab}
            onChange={(value) => setActiveTab(value as 'PENDING' | 'ACTIVE')}
          />
        </CardContent>
      </Card>

      {/* Agreements List */}
      <div className="grid gap-4">
        {displayAgreements.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {activeTab === 'PENDING'
                ? 'Nenhum acordo pendente. Acordos são criados automaticamente quando um incidente com prejuízo é registrado.'
                : 'Nenhum acordo ativo no momento'}
            </CardContent>
          </Card>
        ) : (
          displayAgreements.map((agreement) => (
            <Card key={agreement.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">
                        {agreement.incident?.employee?.user?.name}
                      </CardTitle>
                      <Badge variant={getStatusVariant(agreement.status)}>
                        {STATUS_LABELS[agreement.status as PaymentAgreementStatus]}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {agreement.incident?.category?.name}Criado em{' '}
                      {format(new Date(agreement.createdAt), "dd 'de' MMMM 'de' yyyy", {
                        locale: ptBR,
                      })}
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Prejuízo Total</p>
                        <p className="text-lg font-bold text-red-600">
                          R$ {Number(agreement.totalAmount).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Empresa Absorveu</p>
                        <p className="text-lg font-bold text-green-600">
                          R$ {Number(agreement.companyAbsorbed).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Colaborador</p>
                        <p className="text-lg font-bold text-orange-600">
                          R$ {Number(agreement.employeeResponsible).toFixed(2)}
                        </p>
                      </div>
                      {agreement.status === 'ACTIVE' && (
                        <div>
                          <p className="text-xs text-muted-foreground">Parcelas Pagas</p>
                          <p className="text-lg font-bold">
                            {agreement.paidInstallments}/{agreement.installments}
                          </p>
                        </div>
                      )}
                    </div>

                    {agreement.status === 'PENDING' && (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center gap-2 text-orange-700">
                          <Clock className="w-4 h-4" />
                          <p className="text-sm font-medium">
                            Aguardando definição de parcelas
                          </p>
                        </div>
                      </div>
                    )}

                    {agreement.notes && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Obs: {agreement.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {agreement.status === 'PENDING' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleOpenActivateModal(agreement.id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Definir Parcelas
                      </Button>
                    )}

                    {agreement.status === 'ACTIVE' && (
                      <>
                        {agreement.installments && agreement.paidInstallments < agreement.installments && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenPayModal(agreement.id)}
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            Pagar Parcela
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenCancelModal(agreement.id)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                      </>
                    )}

                    <Link to={`/incident-management/${agreement.incidentId}`}>
                      <Button variant="outline" size="sm" className="w-full">
                        Ver Incidente
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))
        )}
      </div>

      {/* Modal de Pagar Parcela */}
      <ConfirmationModal
        isOpen={showPayModal}
        onClose={() => setShowPayModal(false)}
        onConfirm={handlePayInstallment}
        title="Confirmar Pagamento"
        description="Deseja confirmar o pagamento desta parcela? Esta ação registrará o pagamento no sistema."
        confirmText="Confirmar Pagamento"
        cancelText="Cancelar"
        isLoading={registerPayment.isPending}
        variant="info"
      />

      {/* Modal de Cancelar Acordo */}
      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelAgreement}
        title="Cancelar Acordo"
        description="Tem certeza que deseja cancelar este acordo de pagamento? Esta ação não pode ser desfeita."
        confirmText="Sim, Cancelar Acordo"
        cancelText="Não"
        isLoading={cancelAgreement.isPending}
        variant="danger"
      />

      {/* Modal Ativar Acordo */}
      {showActivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Ativar Acordo de Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                type="number"
                label="Número de Parcelas (máx. 12)"
                placeholder="Ex: 6"
                value={installments}
                onChange={(e) => setInstallments(e.target.value)}
                min="1"
                max="12"
              />

              <Input
                type="date"
                label="Data de Início"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />

              <Input
                label="Observações (opcional)"
                placeholder="Ex: Desconto concedido..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleActivateAgreement}
                  disabled={activatePendingAgreement.isPending || !installments || !startDate}
                >
                  {activatePendingAgreement.isPending ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Ativando...
                    </>
                  ) : (
                    'Ativar Acordo'
                  )}
                </Button>
                <Button variant="outline" onClick={() => setShowActivateModal(false)} disabled={activatePendingAgreement.isPending}>
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
